export const CHANNEL_ROLES = ["broadcaster", "manager", "operator"] as const;
export type ChannelRole = (typeof CHANNEL_ROLES)[number];

/** Generic destinations for host-executed chat output actions. */
export type ChatOutputTarget = "all_chats" | "source_only" | "where_asked";

/**
 * Named role thresholds per docs/decisions/0006-rollenschwellen.md, so a
 * threshold is written once and not as a scattered `role !== "operator"` or
 * a raw `'broadcaster', 'manager'` SQL literal. `canManage` is the shared
 * "verwaltend" threshold from that decision -- both the dashboard (read-only
 * UI gating) and the worker (`db/guards.ts`'s `sqlRole`/`sqlRoleList` render
 * these same sets into SQL) use it, so the two can't drift apart.
 */
export const MANAGING_ROLES = ["broadcaster", "manager"] as const satisfies readonly ChannelRole[];
export const PLATFORM_ASSIGNABLE_ROLES = ["manager", "operator"] as const satisfies readonly ChannelRole[];

export const canManage = (role: ChannelRole): boolean =>
  (MANAGING_ROLES as readonly ChannelRole[]).includes(role);

const CHANNEL_ROLE_RANK: Readonly<Record<ChannelRole, number>> = {
  operator: 0,
  manager: 1,
  broadcaster: 2,
};

/** Checks whether a channel member meets an action's minimum role. */
export const hasRequiredChannelRole = (role: ChannelRole, requiredRole: ChannelRole): boolean =>
  CHANNEL_ROLE_RANK[role] >= CHANNEL_ROLE_RANK[requiredRole];

export type RoleMatrixActionId =
  | "view_channel"
  | "manage_access"
  | "manage_commands"
  | "toggle_commands"
  | "run_voting"
  | "send_shoutouts_clips"
  | "manage_votekicks"
  | "manage_operations"
  | "adjust_counters"
  | "manage_ads"
  | "manage_broadcast"
  | "assign_broadcaster";

export type RoleMatrixAccess = "member" | "operator" | "manager" | "broadcaster";

/** Action thresholds for the member role matrix; manager access shares the worker's MANAGING_ROLES set. */
export const ROLE_MATRIX_ACTIONS: readonly { id: RoleMatrixActionId; access: RoleMatrixAccess }[] = [
  { id: "view_channel", access: "member" },
  { id: "manage_access", access: "manager" },
  { id: "manage_commands", access: "manager" },
  { id: "toggle_commands", access: "operator" },
  { id: "run_voting", access: "operator" },
  { id: "send_shoutouts_clips", access: "operator" },
  { id: "manage_votekicks", access: "operator" },
  { id: "manage_operations", access: "operator" },
  { id: "adjust_counters", access: "manager" },
  { id: "manage_ads", access: "manager" },
  { id: "manage_broadcast", access: "manager" },
  { id: "assign_broadcaster", access: "broadcaster" },
];

/** Same role predicates used by module authorization and channel operations. */
export const roleMatrixAllows = (role: ChannelRole, access: RoleMatrixAccess): boolean => {
  if (access === "member") return CHANNEL_ROLES.includes(role);
  if (access === "operator") return hasRequiredChannelRole(role, "operator");
  if (access === "manager") return canManage(role);
  return role === "broadcaster";
};

export type AuditActorKind = "member" | "platform_admin" | "system";

export const EVENT_TONES = ["info", "warning", "error"] as const;
export type EventTone = (typeof EVENT_TONES)[number];

/** Persisted Twitch stream state exposed to the channel dashboard. */
export type ChannelStreamState = "online" | "offline";

/** Stable catalogue keys for immediate actions that cannot currently run. */
export type ImmediateActionUnavailableReason = "stream_offline" | "stream_state_unknown" | "module_disabled";

/** Durations accepted when a channel member enables an operational brake. */
export const CHANNEL_CONTROL_DURATIONS = ["15m", "1h", "until_stream_end", "unlimited"] as const;
export type ChannelControlDuration = (typeof CHANNEL_CONTROL_DURATIONS)[number];

/** Groups that organize the template-variable catalog and picker. */
export type TemplateVariableGroup = "context" | "stream" | "person" | "command" | "time_random" | "event" | "channel";

/** Template positions supported by the host renderer. */
export type TemplateContext = "chat_command" | "event" | "system";

/** Origins of values shown in template editors. */
export type TemplateVariableSource = "system" | "module" | "channel";

/** Closed operations available to text-command variable actions and dashboard controls. */
export type ChannelVariableOperation = "add" | "subtract" | "set" | "set_argument";

export const CHANNEL_VARIABLE_MAXIMUM_COUNT = 50;
export const CHANNEL_VARIABLE_MINIMUM_VALUE = -999_999_999;
export const CHANNEL_VARIABLE_MAXIMUM_VALUE = 999_999_999;

/** Mirrors the arithmetic in prepareChannelVariableChange without changing D1. */
export const nextVariableValue = (
  currentValue: number,
  operation: ChannelVariableOperation,
  amount: number,
): number => {
  if (operation === "set" || operation === "set_argument") return amount;
  if (operation === "add") {
    return Math.max(CHANNEL_VARIABLE_MINIMUM_VALUE, Math.min(CHANNEL_VARIABLE_MAXIMUM_VALUE, currentValue + amount));
  }
  return Math.max(CHANNEL_VARIABLE_MINIMUM_VALUE, Math.min(CHANNEL_VARIABLE_MAXIMUM_VALUE, currentValue - amount));
};

/** Limits for the first stored-overlay editor iteration. */
export const OVERLAY_MAXIMUM_COUNT = 20;
export const OVERLAY_ELEMENT_MAXIMUM_COUNT = 20;
export type OverlayElementKind = "variable" | `${string}.${string}`;

/** Closed reasons persisted when an overlay access is revoked. */
export const OVERLAY_ACCESS_REVOCATION_REASONS = ["manual", "overlay_deleted", "replaced"] as const;
export type OverlayAccessRevocationReason = (typeof OVERLAY_ACCESS_REVOCATION_REASONS)[number];

/** Stable reasons for a rejected manual or automatic shoutout. */
export const SHOUTOUT_FAILURE_REASONS = [
  "app_token_unavailable",
  "bot_identity_missing",
  "network_error",
  "not_moderator",
  "rate_limited",
  "scope_missing",
  "timeout",
  "twitch_error",
  "twitch_user_not_found",
  "twitch_user_search_failed",
] as const;
export type ShoutoutFailureReason = (typeof SHOUTOUT_FAILURE_REASONS)[number];

/** Stable outcomes for a host moderation action or lifted ban. */
export type ModerationFailureReason =
  | "already_banned"
  | "bot_identity_missing"
  | "conflict"
  | "invalid_request"
  | "network_error"
  | "not_timed_out"
  | "not_moderator"
  | "protected_target"
  | "rate_limited"
  | "scope_missing"
  | "timeout"
  | "token_invalid"
  | "twitch_error";

/** Stable reasons for a rejected Start Commercial request. */
export const COMMERCIAL_FAILURE_REASONS = [
  "app_token_unavailable",
  "network_error",
  "rate_limited",
  "scope_missing",
  "stream_offline",
  "token_invalid",
  "timeout",
  "twitch_error",
] as const;
export type CommercialFailureReason = (typeof COMMERCIAL_FAILURE_REASONS)[number];

/** Stable reasons a `raid.invalid` diagnostic can carry -- see `modules/raid/domain`. */
export const RAID_INVALID_REASONS = ["target_invalid", "source_invalid", "viewers_invalid"] as const;
export type RaidInvalidReason = (typeof RAID_INVALID_REASONS)[number];

/** Stable reasons a `shoutout.suppressed` diagnostic can carry -- see `modules/raid/service`. */
export const SHOUTOUT_SUPPRESSED_REASONS = ["below_threshold", "disabled"] as const;
export type ShoutoutSuppressedReason = (typeof SHOUTOUT_SUPPRESSED_REASONS)[number];

/** Stable reasons an `ads.skipped` diagnostic can carry -- see `modules/ads/domain`. */
export const ADS_SKIPPED_REASONS = ["duration_zero", "duration_invalid", "start_invalid"] as const;
export type AdsSkippedReason = (typeof ADS_SKIPPED_REASONS)[number];

export type IdentityStatus = "connected" | "revoked" | "error";

export type EventSubAuthorizationIdentity =
  | { kind: "bot"; userId: string }
  | { kind: "login"; userId: string };

/**
 * The subscription types the bot maintains with Twitch. They live here
 * instead of in the worker because `modules/channel_events` and
 * `dashboard/module-labels` need them, and the module boundary doesn't let
 * them pull it from `worker/`. The conditions for creating them stay in the
 * worker, though -- only it builds them.
 * `tests/unit/eventsub-types.test.ts` keeps the tuple and table in sync.
 */
export const EVENTSUB_SUBSCRIPTION_TYPES = [
  "automod.message.hold",
  "channel.ad_break.begin",
  "channel.chat.message",
  "channel.chat.notification",
  "channel.moderate",
  "channel.raid",
  "channel.shoutout.create",
  "channel.shoutout.receive",
  "channel.suspicious_user.message",
  "channel.suspicious_user.update",
  "channel.update",
  "channel.vip.remove",
  "stream.offline",
  "stream.online",
] as const;
export type EventSubSubscriptionType = (typeof EVENTSUB_SUBSCRIPTION_TYPES)[number];

export const EVENTSUB_NEUTRAL_REASON_CODES = ["moderator_required", "pending_adoption", "verification_pending"] as const;
export type EventSubNeutralReasonCode = (typeof EVENTSUB_NEUTRAL_REASON_CODES)[number];

/**
 * Reason and error codes the worker writes to EventSub, bot and login maintenance rows (its own codes, Twitch
 * revocation statuses and Twitch error strings passed through). The dashboard catalogue is typed over this list,
 * and a unit test reads it, so a code added here without a de and en text fails the build.
 */
export const MAINTENANCE_REASON_CODES = [
  "timeout", "network_error", "invalid_response", "invalid_target", "pagination_loop", "maintenance_failed",
  "moderator_status_failed", "channel_or_consent_missing", "token_ciphertext_unreadable", "validate_failed",
  "refresh_failed", "invalid_grant", "rate_limited", "subscription_replaced", "missing_scope",
  "bot_identity_missing", "bot_identity_revoked",
  "authorization_revoked", "user_removed", "moderator_removed", "chat_user_banned", "notification_failures_exceeded",
  "version_removed", "beta_maintenance", "webhook_callback_verification_pending", "webhook_callback_verification_failed",
  "Unauthorized", "Forbidden", "Bad Request", "Conflict", "Too Many Requests",
] as const;
export type MaintenanceReasonCode = (typeof MAINTENANCE_REASON_CODES)[number];

export const isEventSubSubscriptionType = (value: string): value is EventSubSubscriptionType =>
  (EVENTSUB_SUBSCRIPTION_TYPES as readonly string[]).includes(value);

/**
 * The diagnostic codes modules and the host write to `event_log.code`.
 * Closed on purpose, the same reasoning as `ModuleDiagnosticDetailKey` in
 * `modules/contract.ts`: a scanner only sees the shapes it was taught, but a
 * union is checked by the compiler at every construction site, however the
 * object is assembled. `dashboard/locale.ts`'s `eventTexts`/`eventToneEntries`
 * are each `Record<EventCode, ...>`, so adding, renaming or removing a code
 * here forces both language catalogues to keep up or the build fails.
 * `tests/unit/event-codes.test.ts` adds a runtime check on top (a cast could
 * still slip a value past the type).
 */
export const EVENT_CODES = [
  "host.action.failed",
  "host.action.suppressed",
  "host.chat.failed",
  "host.chat.sent",
  "host.chat.skipped",
  "host.announcement.failed",
  "host.announcement.sent",
  "template_truncated",
  "host.module.error",
  "host.module.unknown",
  "host.overlay.not_executed",
  "host.shoutout.failed",
  "host.shoutout.sent",
  "host.timeout.applied",
  "host.timeout.failed",
  "host.timeout.ambiguous",
  "host.ban.applied",
  "host.ban.failed",
  "host.ban.ambiguous",
  "channel_events.raid.incoming",
  "channel_events.raid.outgoing",
  "channel_events.shoutout.sent",
  "channel_events.shoutout.received",
  "channel_events.chat.sub",
  "channel_events.chat.resub",
  "channel_events.chat.gift_sub",
  "channel_events.chat.community_gift",
  "channel_events.chat.announcement",
  "channel_events.chat.unknown",
  "channel_events.moderation.ban",
  "channel_events.moderation.timeout",
  "channel_events.moderation.untimeout",
  "channel_events.moderation.unban",
  "channel_events.moderation.delete",
  "channel_events.moderation.warn",
  "channel_events.moderation.unknown",
  "channel_events.automod.held",
  "channel_events.suspicious.message",
  "channel_events.suspicious.classified",
  "channel_events.suspicious.cleared",
  "channel_events.stream.offline",
  "channel_events.stream.online",
  "raid.outgoing",
  "raid.shoutout",
  "raid.invalid",
  "shoutout.suppressed",
  "ads.announcement",
  "ads.skipped",
  "ads.prewarning.announced",
  "ads.prewarning.no_schedule",
  "ads.prewarning.too_late",
  "ads.prewarning.break_started",
  "ads.prewarning.rescheduled",
  "ads.prewarning.scope_missing",
  "ads.prewarning.schedule_error",
  "ads.snooze",
  "ads.commercial.failed",
  "host.clip.failed",
  "belabox.fetch_failing",
  "belabox.fetch_recovered",
  "belabox.alert_started",
  "belabox.alert_escalated",
  "belabox.alert_recovered",
  "template.deferred_live",
  "template.lookup_unavailable",
  "template.chat_reference_dropped",
  "text_commands.cooldown",
  "text_commands.user_cooldown",
  "text_commands.stream_state",
  "text_commands.game_filter",
  "text_commands.game_unknown",
  "text_commands.triggered",
  "text_commands.disabled",
  "text_commands.permission_denied",
  // Since the removal of the mutating chat commands (#120), nobody generates
  // these two codes anymore. They stay because the event log keeps its rows
  // for 14 days: without a label, entries already written would become
  // unreadable in the panel.
  "text_commands.already_exists",
  "text_commands.not_authorized",
  "text_commands.unknown",
  "text_commands.invalid",
  "text_commands.lookup_unavailable",
  "text_commands.argument_missing",
  "text_commands.argument_invalid",
  "text_commands.changed_concurrently",
  "text_commands.variable_update_failed",
  "temp_vips.granted",
  "temp_vips.extended",
  "temp_vips.imported",
  "temp_vips.ended",
  "temp_vips.released",
  "temp_vips.removal_failed",
  "temp_vips.classified",
  "temp_vips.expire_reminder",
  "votekick.rejected",
  "template_parameters_invalid",
] as const;
export type EventCode = (typeof EVENT_CODES)[number];

/**
 * The `audit_log.action` vocabulary, closed for the same reason as
 * `EVENT_CODES` above. `channel.*`/`member.*` are also `PlatformAction` in
 * `worker/platform/repository.ts`; every fixed action is localized in the
 * dashboard audit labels. A module-specific `${moduleId}.settings_changed`
 * action keeps its identifier in the per-channel log. `AuditWriteAction`
 * below keeps both forms checked at the D1 writer.
 */
export const AUDIT_ACTIONS = [
  "channel.released",
  "channel.full_consent_changed",
  "channel.time_zone.updated",
  "channel.default_chat_target.updated",
  "channel.location.updated",
  "channel.broadcast.updated",
  "member.added",
  "member.role_changed",
  "member.removed",
  "module.enabled",
  "module.disabled",
  "module.secret.replaced",
  "module.secret.removed",
  "points_adjusted",
  "chat_voting.started",
  "chat_voting.closed",
  "chat_voting.extended",
  "chat_voting.template.created",
  "chat_voting.template.updated",
  "chat_voting.template.deleted",
  "text_commands.command.created",
  "text_commands.command.updated",
  "text_commands.command.removed",
  "faq.entry.created",
  "faq.entry.updated",
  "faq.entry.removed",
  "faq.entry.enabled",
  "faq.entry.disabled",
  "faq.entry.reordered",
  "blitz_round.question.created",
  "blitz_round.question.updated",
  "blitz_round.question.deleted",
  "blitz_round.started",
  "quiz.set.created",
  "quiz.set.updated",
  "quiz.set.deleted",
  "duel.round.cancelled",
  "text_library.block.created",
  "text_library.block.updated",
  "text_library.block.renamed",
  "text_library.block.removed",
  "text_library.category.created",
  "text_library.category.renamed",
  "text_library.category.removed",
  "text_library.settings.updated",
  "timers.timer.created",
  "timers.timer.updated",
  "timers.timer.removed",
  "timers.timer.enabled",
  "timers.timer.disabled",
  "timers.timer.skipped",
  "api_source.source_created",
  "api_source.source_changed",
  "api_source.source_deleted",
  "channel.variable.created",
  "channel.variable.renamed",
  "channel.variable.removed",
  "channel.variable.value_changed",
  "ads.commercial_started",
  "clip.created",
  "channel.mute.enabled",
  "channel.mute.disabled",
  "channel.pause.enabled",
  "channel.pause.disabled",
  "votekick.cancelled",
  "votekick.timeout_lift_attempted",
  "overlay.token.issued",
  "overlay.token.revoked",
  "overlay.access.issued",
  "overlay.access.revealed",
  "overlay.access.revoked",
  "overlay.access.replaced",
  "overlay.access.removed",
  "overlay.created",
  "overlay.updated",
  "overlay.deleted",
  "overlay.legacy.imported",
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];
type ModuleSettingsChangedAction = `${string}.settings_changed`;
export type AuditWriteAction = AuditAction | ModuleSettingsChangedAction;

/**
 * The audit log's "area" filter (#181): every `AuditWriteAction` sorts into
 * exactly one of these by its prefix (`dashboard/audit/model.ts`'s
 * `auditAreaForAction`), shared verbatim by the worker's SQL predicate
 * (`worker/panel/repository.ts`) so the filter and the family icon can't
 * drift apart.
 */
export const AUDIT_AREAS = ["module", "command", "member", "channel", "overlay"] as const;
export type AuditArea = (typeof AUDIT_AREAS)[number];

/**
 * The closed vocabulary of `{ "error": "<code>" }` responses the worker
 * (and, client-side, `dashboard/api.ts`'s own request guard) sends instead
 * of German prose. `dashboard/locale.ts`'s `apiErrorTexts` is a
 * `Record<ApiErrorCode, string>` per language, the same reasoning as
 * `EVENT_CODES` above: the compiler refuses a build where a code here has
 * no catalogue entry. An unrecognized or missing code (old worker, a proxy's
 * HTML error page, a network failure) is not a type error at the response
 * boundary -- `PanelApiError.code` is `string | null` -- so the dashboard
 * falls back to a generic, per-call-site localized text instead of ever
 * showing raw server text.
 */
export type ApiErrorCode =
  | "session_missing"
  | "websocket_origin_invalid"
  | "realtime_protocol_unsupported"
  | "csrf_invalid"
  | "channel_missing"
  | "channel_access_denied"
  | "platform_access_denied"
  | "panel_request_not_allowed"
  | "pagination_limit_invalid"
  | "pagination_cursor_invalid"
  | "twitch_login_invalid"
  | "twitch_user_not_found"
  | "twitch_user_search_failed"
  | "channel_not_found"
  | "broadcaster_role_immutable"
  | "broadcaster_role_change_requires_broadcaster"
  | "mutation_failed"
  | "member_changed_concurrently"
  | "release_input_invalid"
  | "channel_already_released"
  | "channel_release_failed"
  | "full_consent_invalid"
  | "full_consent_already_set"
  | "member_or_role_invalid"
  | "member_already_exists"
  | "member_management_denied"
  | "self_membership_denied"
  | "member_add_failed"
  | "role_invalid"
  | "member_not_found"
  | "role_already_set"
  | "self_role_escalation_denied"
  | "last_broadcaster_cannot_be_demoted"
  | "last_broadcaster_cannot_be_removed"
  | "event_origin_invalid"
  | "event_tone_invalid"
  | "audit_area_invalid"
  | "moderator_status_check_denied"
  | "moderator_status_check_rate_limited"
  | "moderator_status_check_failed"
  | "module_management_denied"
  | "module_unknown"
  | "module_not_configured"
  | "module_disabled"
  | "module_settings_invalid"
  | "module_settings_changed_concurrently"
  | "module_enabled_field_invalid"
  | "module_mandatory"
  | "module_changed_concurrently"
  | "command_management_denied"
  | "command_data_invalid"
  | "command_already_exists"
  | "command_alias_conflict"
  | "command_creation_denied"
  | "command_not_found"
  | "command_update_denied"
  | "command_changed_concurrently"
  | "command_delete_denied"
  | "variable_data_invalid"
  | "variable_not_found"
  | "variable_already_exists"
  | "variable_limit_reached"
  | "variable_in_use"
  | "variable_changed_concurrently"
  | "variable_rename_template_too_long"
  | "variable_rename_overlay_text_too_long"
  | "variable_management_denied"
  | "variable_value_change_denied"
  | "ad_schedule_read_failed"
  | "ad_snooze_denied"
  | "ad_snooze_failed"
  | "commercial_length_invalid"
  | "commercial_start_denied"
  | "commercial_start_failed"
  | "commercial_stream_offline"
  | "clip_create_failed"
  | "clip_stream_offline"
  | "channel_control_input_invalid"
  | "channel_control_changed_concurrently"
  | "shoutout_send_failed"
  | "overlay_token_manage_denied"
  | "overlay_expiry_invalid"
  | "overlay_revocation_reason_invalid"
  | "overlay_token_not_found"
  | "overlay_token_invalid"
  | "overlay_variable_not_found"
  | "overlay_management_denied"
  | "overlay_css_invalid"
  | "overlay_data_invalid"
  | "overlay_variable_reference_invalid"
  | "overlay_not_found"
  | "overlay_token_already_bound"
  | "overlay_limit_reached"
  | "overlay_element_limit_reached"
  | "overlay_changed_concurrently"
  | "unknown_api_route";
